/** Copyright 2025 Midas Healthcare Solutions - All Rights Reserved **/
import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { RxJSBaseClass } from '@midas/shared/util';
import {
  auditTime,
  BehaviorSubject,
  debounceTime,
  filter,
  finalize,
  fromEvent,
  Observable,
  startWith,
  Subscription,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { PagedResult } from './ui-query-select';

@Directive()
export abstract class IQuerySingle<T extends { id: number }>
  extends RxJSBaseClass
  implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor
{
  @Input() disabled = false;
  @Output() itemSelected = new EventEmitter<{ item: T | null }>();

  readonly searchCtrl = new FormControl('');

  filteredItems!: Observable<T[]>;

  item: T | null = null;
  total = Number.MAX_VALUE;

  private options: T[] = [];
  private options$ = new BehaviorSubject<T[]>([]);
  private pageSize = 10;
  loading = false;
  private currentQuery = '';

  @ViewChild('auto') auto!: MatAutocomplete;
  private panelScrollSub?: Subscription;
  changeFn!: (val: number | string | null) => unknown;

  abstract getItemsBySearchString(
    searchString?: string | null,
    startingIndex?: number,
    numberOfRecords?: number,
  ): Observable<PagedResult<T>>;
  abstract getItemById(id: number): Observable<T | null>;
  abstract getDisplayName(item: T): string;
  abstract getPlaceholderText(): string;

  constructor(private change: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    this.filteredItems = this.options$.asObservable();

    this.searchCtrl.valueChanges
      .pipe(
        debounceTime(250),
        filter((str) => typeof str === 'string'),
        startWith(null),
        switchMap((str) => {
          this.currentQuery = str || '';
          this.options = [];
          this.options$.next(this.options);
          return this.getItemsBySearchString(this.currentQuery, 0, this.pageSize);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((pagedResults) => {
        this.options = pagedResults.items ?? [];
        this.options$.next(this.options);
        this.total = pagedResults.count;
        this.change.markForCheck();
      });

    if (this.disabled) this.searchCtrl.disable();
  }

  ngAfterViewInit(): void {
    this.auto.opened.pipe(takeUntil(this.destroy$)).subscribe(() => this.onPanelOpened());
    this.auto.closed.pipe(takeUntil(this.destroy$)).subscribe(() => this.onPanelClosed());
  }

  onPanelOpened() {
    const panel = document.getElementById(this.auto.id) as HTMLElement | null;
    if (!panel) return;

    this.panelScrollSub = fromEvent(panel, 'scroll')
      .pipe(auditTime(0), takeUntil(this.auto.closed))
      .subscribe(() => {
        if (panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 48) this.loadMore();
      });
  }

  onPanelClosed() {
    this.panelScrollSub?.unsubscribe();
    this.panelScrollSub = undefined;
  }

  private loadMore() {
    if (this.loading || this.options.length >= this.total) return;

    this.loading = true;
    this.getItemsBySearchString(this.currentQuery, this.options.length, this.pageSize)
      .pipe(
        finalize(() => (this.loading = false)),
        take(1),
      )
      .subscribe((nextPage) => {
        if (!nextPage || nextPage.items.length === 0) return;
        this.options = this.options.concat(nextPage.items);
        this.options$.next(this.options);
        this.change.markForCheck();
      });
  }

  override ngOnDestroy(): void {
    this.panelScrollSub?.unsubscribe();
    super.ngOnDestroy?.();
  }

  validateSelection() {
    if (!this.item || this.getDisplayName(this.item) !== this.searchCtrl.value) {
      this.item = null;
      this.changeFn(null);
      this.searchCtrl.setValue('');
    }
  }

  clear(): void {
    this.item = null;
    this.changeFn(null);
    this.searchCtrl.setValue('');
  }

  getValue(item: T): number | string {
    return item.id;
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.item = event.option.value as T;
    this.changeFn(this.getValue(this.item));
    this.searchCtrl.setValue(this.getDisplayName(this.item));
    this.itemSelected.emit({ item: this.item });
  }

  writeValue(id: number): void {
    if (!id) {
      this.item = null;
      return;
    }

    this.getItemById(id)
      .pipe(take(1))
      .subscribe((item) => {
        this.item = item;
        if (this.item) this.searchCtrl.setValue(this.getDisplayName(this.item));
        this.change.markForCheck();
      });
  }

  registerOnChange(fn: (val: number | string | null) => unknown): void {
    this.changeFn = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnTouched(fn: any): void {
    this.searchCtrl.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(fn);
  }
}
